import { apiService } from './api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Decodes the base64 payload the GetFile endpoints return. Tolerates the
 * surrounding quotes left when the JSON string literal was read as text.
 */
export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer>
{
  let payload = base64.trim();
  if (payload.startsWith('"') && payload.endsWith('"'))
  {
    payload = payload.slice(1, -1);
  }

  const binaryString = atob(payload);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++)
  {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function base64ToBlob(base64: string, mimeType: string): Blob
{
  return new Blob([base64ToBytes(base64)], { type: mimeType });
}

function triggerDownload(blob: Blob, fileName: string): void
{
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Chrome ignores `download` on a cross-origin href, so serve from a blob.
async function downloadFromUrl(fileUrl: string, fileName: string): Promise<void>
{
  if (!fileUrl) return;

  try
  {
    const response = await fetch(fileUrl, { credentials: 'include' });
    if (!response.ok) throw new Error(`Request failed with ${response.status}`);

    triggerDownload(await response.blob(), fileName);
  }
  catch
  {
    window.open(fileUrl, '_blank', '');
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Mark or unmark an attachment as favourite.
 */
export async function updateFavoriteAttachment(
  attachmentId: number,
  embeddedImageId: number,
  isFavorite: boolean,
): Promise<void>
{
  await apiService.put(
    `Conversation/UpdateFavoriteAttachment?AttachmentId=${attachmentId}&EmbeddedImageId=${embeddedImageId}&isFavorite=${isFavorite}`,
  );
}

/**
 * Raw bytes of a conversation attachment, served through our API instead of
 * straight from storage - the bucket does not allow CORS, so page code cannot
 * read FileUrl itself.
 */
export async function fetchAttachmentBytes(
  attachmentId: number,
  embeddedImageId?: number | null,
): Promise<Uint8Array<ArrayBuffer>>
{
  const httpPath = Number(embeddedImageId) > 0
    ? `EmbebbedImages/GetFile?attachmentId=${embeddedImageId}`
    : `attachment/GetFile?attachmentId=${attachmentId}`;

  const response = await apiService.get<string>(httpPath, { responseType: 'text' });
  const data = typeof response === 'string' ? response : JSON.stringify(response);

  return base64ToBytes(JSON.parse(data));
}

/**
 * Download a single image.
 * Replicates legacy FileService.DownloadFile:
 *   - attachmentId > 0 → api/attachment/GetFile?attachmentId=
 *   - embeddedImageId > 0 → api/EmbebbedImages/GetFile?attachmentId=
 *   - neither (chat files) → fetched from fileUrl
 * The backend returns base64-encoded content; we decode it and trigger a local download.
 */
export async function downloadFile(
  attachmentId: number,
  embeddedImageId: number,
  fileName: string,
  fileUrl: string,
): Promise<void>
{
  let httpPath = '';

  if (Number(attachmentId) > 0)
  {
    httpPath = `attachment/GetFile?attachmentId=${attachmentId}`;
  }
  if (Number(embeddedImageId) > 0)
  {
    httpPath = `EmbebbedImages/GetFile?attachmentId=${embeddedImageId}`;
  }

  // Chat files carry neither id - they are served straight from FileUrl.
  if (!httpPath)
  {
    await downloadFromUrl(fileUrl, fileName);
    return;
  }

  try
  {
    const response = await apiService.get<string>(httpPath, { responseType: 'text' });

    const data = typeof response === 'string' ? response : JSON.stringify(response);
    const parsed = JSON.parse(data);

    const contentType = 'application/octet-stream';
    const blob = base64ToBlob(parsed, contentType);

    triggerDownload(blob, fileName);
  }
  catch
  {
    // Fallback: pull the file straight from its URL
    await downloadFromUrl(fileUrl, fileName);
  }
}

/**
 * Download all selected images as a ZIP.
 * Replicates legacy FileService.DownloadAllFiles:
 *   - Sends comma-separated string IDs (not arrays)
 */
export async function downloadAllFiles(
  attachments: Array<{ AttachmentId: number; EmbeddedImageId?: number }>,
): Promise<void>
{
  const attachmentIds: string[] = [];
  const embeddedImageIds: string[] = [];

  for (const file of attachments)
  {
    if (file.AttachmentId > 0)
    {
      attachmentIds.push(String(file.AttachmentId));
    }
    if (file.EmbeddedImageId && file.EmbeddedImageId > 0)
    {
      embeddedImageIds.push(String(file.EmbeddedImageId));
    }
  }

  const request = {
    AttachmentIds: attachmentIds.join(','),
    EmbeddedImageIds: embeddedImageIds.join(','),
  };

  const buffer: ArrayBuffer = await apiService.post<ArrayBuffer>(
    'attachment/PostMultipleFiles',
    request,
    { responseType: 'arraybuffer' },
  );

  const now = new Date();
  const fileName = `images_${now.getFullYear()}_${now.getMonth() + 1}_${now.getDate()}.zip`;
  const blob = new Blob([buffer], { type: 'application/zip' });
  triggerDownload(blob, fileName);
}
